import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Filter, Upload, Download, Eye, Edit2, Trash2, 
  Clock, CheckCircle, AlertCircle, FileCheck, Send, Archive, History,
  Users, RefreshCw, ChevronRight, MoreHorizontal, BookOpen, FolderOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Status badge styling
const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-500/10 text-gray-600 dark:text-gray-400', icon: Edit2 },
  under_review: { label: 'Under Review', color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', icon: CheckCircle },
  published: { label: 'Published', color: 'bg-green-500/10 text-green-600 dark:text-green-400', icon: FileCheck },
  superseded: { label: 'Superseded', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: History },
  archived: { label: 'Archived', color: 'bg-slate-500/10 text-slate-600 dark:text-slate-400', icon: Archive },
  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-600 dark:text-red-400', icon: AlertCircle },
};

// Category groups
const categoryGroups = {
  'Operations Manuals': ['om_a', 'om_b', 'om_c', 'om_d'],
  'Maintenance': ['mel', 'mmel', 'came', 'moe'],
  'Safety & Quality': ['sms_manual', 'qms_manual', 'srm', 'erp'],
  'Training': ['training_manual', 'crm_manual'],
  'Procedures': ['sop', 'checklist', 'form', 'policy'],
  'Communications': ['circular', 'notice'],
  'Other': ['other'],
};

export default function DocumentsPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [versions, setVersions] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    document_number: '',
    category: '',
    description: '',
    regulatory_reference: '',
    department: '',
    review_cycle_months: 12,
    requires_acknowledgment: false,
    tags: ''
  });

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
    fetchStats();
  }, [api]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/documents/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/documents/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      
      await api.post('/documents', payload);
      toast.success('Document created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDocument = async (e) => {
    e.preventDefault();
    if (!selectedDocument) return;
    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      };
      
      await api.put(`/documents/${selectedDocument.id}`, payload);
      toast.success('Document updated successfully');
      setShowViewDialog(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprovalAction = async (action) => {
    if (!selectedDocument) return;
    setIsSubmitting(true);
    
    try {
      await api.post(`/documents/${selectedDocument.id}/approval`, {
        action: action,
        comments: formData.comments || null
      });
      
      toast.success(`Document ${action.replace('_', ' ')} successfully`);
      setShowApprovalDialog(false);
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted successfully');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleUploadFile = async () => {
    if (!selectedDocument || !uploadFile) return;
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      await api.post(`/documents/${selectedDocument.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('File uploaded successfully');
      setShowUploadDialog(false);
      setUploadFile(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadFile = async (doc) => {
    try {
      // First get the latest version to find the file
      const versionsRes = await api.get(`/documents/${doc.id}/versions`);
      const latestVersion = versionsRes.data?.[0];
      
      if (!latestVersion?.file_url) {
        toast.error('No file uploaded for this document');
        return;
      }
      
      // Download the file
      const response = await api.get(latestVersion.file_url, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = latestVersion.file_name || `${doc.document_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error('No file available or download failed');
    }
  };

  const openVersionsDialog = async (doc) => {
    setSelectedDocument(doc);
    try {
      const response = await api.get(`/documents/${doc.id}/versions`);
      setVersions(response.data || []);
      setShowVersionsDialog(true);
    } catch (error) {
      toast.error('Failed to load version history');
    }
  };

  const openUploadDialog = (doc) => {
    setSelectedDocument(doc);
    setUploadFile(null);
    setShowUploadDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      document_number: '',
      category: '',
      description: '',
      regulatory_reference: '',
      department: '',
      review_cycle_months: 12,
      requires_acknowledgment: false,
      tags: ''
    });
    setSelectedDocument(null);
  };

  const openEditDialog = (doc) => {
    setSelectedDocument(doc);
    setFormData({
      title: doc.title,
      document_number: doc.document_number,
      category: doc.category,
      description: doc.description || '',
      regulatory_reference: doc.regulatory_reference || '',
      department: doc.department || '',
      review_cycle_months: doc.review_cycle_months || 12,
      requires_acknowledgment: doc.requires_acknowledgment || false,
      tags: doc.tags?.join(', ') || ''
    });
    setShowViewDialog(true);
  };

  const openApprovalDialog = (doc, action) => {
    setSelectedDocument(doc);
    setFormData({ ...formData, comments: '' });
    setShowApprovalDialog(true);
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchQuery || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.document_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    const matchesTab = activeTab === 'all' || 
      (activeTab === 'pending' && (doc.status === 'under_review' || doc.pending_acknowledgments > 0)) ||
      (activeTab === 'expiring' && doc.expiry_date && new Date(doc.expiry_date) < new Date(Date.now() + 30*24*60*60*1000));
    
    return matchesSearch && matchesCategory && matchesStatus && matchesTab;
  });

  // Get category label
  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat?.label || value;
  };

  return (
    <div className="space-y-6 p-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Document Control System
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage aviation manuals, procedures, and controlled documents
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-document-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Document
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Documents</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.total_documents}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Pending Review</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending_reviews}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Expiring Soon</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.expiring_soon}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-orange-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Expired</p>
                  <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Pending Ack.</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.pending_acknowledgments}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Recent Updates</p>
                  <p className="text-2xl font-bold text-green-600">{stats.recent_updates}</p>
                </div>
                <RefreshCw className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full lg:w-auto">
              <TabsList>
                <TabsTrigger value="all" data-testid="tab-all">All Documents</TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending">
                  Pending Action
                  {stats && (stats.pending_reviews + stats.pending_acknowledgments) > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center rounded-full text-xs">
                      {stats.pending_reviews + stats.pending_acknowledgments}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="expiring" data-testid="tab-expiring">Expiring Soon</TabsTrigger>
              </TabsList>
            </Tabs>
            
            <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              <div className="relative flex-1 lg:flex-none lg:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40" data-testid="filter-category">
                  <FolderOpen className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(categoryGroups).map(([group, cats]) => (
                    <div key={group}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{group}</div>
                      {cats.map(cat => {
                        const category = categories.find(c => c.value === cat);
                        return category && (
                          <SelectItem key={cat} value={cat}>{category.label}</SelectItem>
                        );
                      })}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36" data-testid="filter-status">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">Loading documents...</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="mt-2 text-muted-foreground">No documents found</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Document
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {filteredDocuments.map(doc => {
                const status = statusConfig[doc.status] || statusConfig.draft;
                const StatusIcon = status.icon;
                
                return (
                  <div 
                    key={doc.id} 
                    className="p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`document-item-${doc.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono text-sm font-semibold text-blue-600">
                            {doc.document_number}
                          </span>
                          <Badge className={`${status.color} gap-1`}>
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </Badge>
                          {doc.requires_acknowledgment && doc.pending_acknowledgments > 0 && (
                            <Badge variant="outline" className="text-purple-600 border-purple-300">
                              <Users className="h-3 w-3 mr-1" />
                              {doc.pending_acknowledgments} pending
                            </Badge>
                          )}
                        </div>
                        
                        <h3 className="font-medium text-base truncate">
                          {doc.title}
                        </h3>
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <FolderOpen className="h-3.5 w-3.5" />
                            {getCategoryLabel(doc.category)}
                          </span>
                          <span>Rev. {doc.current_revision}</span>
                          {doc.department && <span>{doc.department}</span>}
                          {doc.regulatory_reference && (
                            <span className="text-blue-600">{doc.regulatory_reference}</span>
                          )}
                        </div>
                        
                        {doc.description && (
                          <p className="mt-2 text-sm text-muted-foreground line-clamp-1">
                            {doc.description}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openEditDialog(doc)}
                          data-testid={`view-doc-${doc.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`actions-${doc.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(doc)}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openUploadDialog(doc)}>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload File
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadFile(doc)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openVersionsDialog(doc)}>
                              <History className="h-4 w-4 mr-2" />
                              Version History
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            
                            {/* Workflow Actions */}
                            {doc.status === 'draft' && (
                              <DropdownMenuItem onClick={() => openApprovalDialog(doc, 'submit_for_review')}>
                                <Send className="h-4 w-4 mr-2" />
                                Submit for Review
                              </DropdownMenuItem>
                            )}
                            {doc.status === 'under_review' && (
                              <>
                                <DropdownMenuItem onClick={() => handleApprovalAction('approve')}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleApprovalAction('reject')}>
                                  <AlertCircle className="h-4 w-4 mr-2" />
                                  Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            {doc.status === 'approved' && (
                              <DropdownMenuItem onClick={() => handleApprovalAction('publish')}>
                                <FileCheck className="h-4 w-4 mr-2" />
                                Publish
                              </DropdownMenuItem>
                            )}
                            {doc.status !== 'archived' && (
                              <DropdownMenuItem onClick={() => handleApprovalAction('archive')}>
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDeleteDocument(doc.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Document Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create New Document
            </DialogTitle>
            <DialogDescription>
              Add a new controlled document to the document management system
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateDocument} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="document_number">Document Number *</Label>
                <Input
                  id="document_number"
                  value={formData.document_number}
                  onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                  placeholder="e.g., OM-A-001"
                  required
                  data-testid="input-doc-number"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({...formData, category: v})}
                  required
                >
                  <SelectTrigger data-testid="select-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categoryGroups).map(([group, cats]) => (
                      <div key={group}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">{group}</div>
                        {cats.map(cat => {
                          const category = categories.find(c => c.value === cat);
                          return category && (
                            <SelectItem key={cat} value={cat}>{category.label}</SelectItem>
                          );
                        })}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="title">Document Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Operations Manual Part A - General/Basic"
                required
                data-testid="input-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of the document contents and purpose"
                rows={3}
                data-testid="input-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="regulatory_reference">Regulatory Reference</Label>
                <Input
                  id="regulatory_reference"
                  value={formData.regulatory_reference}
                  onChange={(e) => setFormData({...formData, regulatory_reference: e.target.value})}
                  placeholder="e.g., GACA CAR Part 121.135"
                  data-testid="input-regulatory-ref"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select 
                  value={formData.department} 
                  onValueChange={(v) => setFormData({...formData, department: v})}
                >
                  <SelectTrigger data-testid="select-department">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                    <SelectItem value="Safety">Safety</SelectItem>
                    <SelectItem value="Quality">Quality</SelectItem>
                    <SelectItem value="Training">Training</SelectItem>
                    <SelectItem value="Compliance">Compliance</SelectItem>
                    <SelectItem value="Flight Operations">Flight Operations</SelectItem>
                    <SelectItem value="Ground Operations">Ground Operations</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="review_cycle">Review Cycle (months)</Label>
                <Select 
                  value={formData.review_cycle_months.toString()} 
                  onValueChange={(v) => setFormData({...formData, review_cycle_months: parseInt(v)})}
                >
                  <SelectTrigger data-testid="select-review-cycle">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months (Annual)</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                    <SelectItem value="36">36 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({...formData, tags: e.target.value})}
                  placeholder="safety, operations, required"
                  data-testid="input-tags"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_acknowledgment"
                checked={formData.requires_acknowledgment}
                onChange={(e) => setFormData({...formData, requires_acknowledgment: e.target.checked})}
                className="h-4 w-4 rounded border-gray-300"
                data-testid="checkbox-acknowledgment"
              />
              <Label htmlFor="requires_acknowledgment" className="font-normal">
                Require acknowledgment from distribution list when published
              </Label>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="submit-create-btn">
                {isSubmitting ? 'Creating...' : 'Create Document'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Edit Document Dialog */}
      <Dialog open={showViewDialog} onOpenChange={(open) => {
        setShowViewDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Edit Document
            </DialogTitle>
            <DialogDescription>
              Update document metadata and properties
            </DialogDescription>
          </DialogHeader>
          
          {selectedDocument && (
            <form onSubmit={handleUpdateDocument} className="space-y-6">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <span className="font-mono font-semibold text-blue-600">
                  {selectedDocument.document_number}
                </span>
                <Badge className={statusConfig[selectedDocument.status]?.color}>
                  {statusConfig[selectedDocument.status]?.label}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Rev. {selectedDocument.current_revision}
                </span>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-title">Document Title *</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-regulatory">Regulatory Reference</Label>
                  <Input
                    id="edit-regulatory"
                    value={formData.regulatory_reference}
                    onChange={(e) => setFormData({...formData, regulatory_reference: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-department">Department</Label>
                  <Select 
                    value={formData.department} 
                    onValueChange={(v) => setFormData({...formData, department: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Operations">Operations</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="Safety">Safety</SelectItem>
                      <SelectItem value="Quality">Quality</SelectItem>
                      <SelectItem value="Training">Training</SelectItem>
                      <SelectItem value="Compliance">Compliance</SelectItem>
                      <SelectItem value="Flight Operations">Flight Operations</SelectItem>
                      <SelectItem value="Ground Operations">Ground Operations</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Review Cycle</Label>
                  <Select 
                    value={formData.review_cycle_months.toString()} 
                    onValueChange={(v) => setFormData({...formData, review_cycle_months: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                      <SelectItem value="36">36 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    placeholder="Comma separated"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-requires-ack"
                  checked={formData.requires_acknowledgment}
                  onChange={(e) => setFormData({...formData, requires_acknowledgment: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="edit-requires-ack" className="font-normal">
                  Require acknowledgment
                </Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowViewDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Action Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Workflow Action</DialogTitle>
            <DialogDescription>
              Add comments for this workflow action (optional)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Textarea
              value={formData.comments || ''}
              onChange={(e) => setFormData({...formData, comments: e.target.value})}
              placeholder="Enter comments..."
              rows={4}
            />
          </div>
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancel
            </Button>
            {selectedDocument?.status === 'draft' && (
              <Button onClick={() => handleApprovalAction('submit_for_review')}>
                <Send className="h-4 w-4 mr-2" />
                Submit for Review
              </Button>
            )}
            {selectedDocument?.status === 'under_review' && (
              <>
                <Button variant="destructive" onClick={() => handleApprovalAction('reject')}>
                  Reject
                </Button>
                <Button onClick={() => handleApprovalAction('approve')}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
            {selectedDocument?.status === 'approved' && (
              <Button onClick={() => handleApprovalAction('publish')}>
                <FileCheck className="h-4 w-4 mr-2" />
                Publish
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload File Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Document File
            </DialogTitle>
            <DialogDescription>
              Upload a file for {selectedDocument?.document_number}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Click to select a file or drag and drop
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, Word, Excel, PowerPoint (max 50MB)
                </p>
              </label>
            </div>
            
            {uploadFile && (
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="flex-1 truncate">{uploadFile.name}</span>
                <span className="text-xs text-muted-foreground">
                  {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
                <Button variant="ghost" size="sm" onClick={() => setUploadFile(null)}>
                  ✕
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUploadFile} disabled={!uploadFile || isUploading}>
              {isUploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Version History
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.document_number} - {selectedDocument?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {versions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No versions found</p>
              </div>
            ) : (
              versions.map((version, idx) => (
                <div key={version.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant={idx === 0 ? "default" : "secondary"}>
                        Rev. {version.version_number}
                      </Badge>
                      <span className="text-sm font-medium">{version.change_summary || 'Version created'}</span>
                    </div>
                    {version.file_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${version.file_url}`, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {version.change_details && <p className="mb-1">{version.change_details}</p>}
                    <p>Created: {new Date(version.created_at).toLocaleString()}</p>
                    {version.file_name && <p>File: {version.file_name}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVersionsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
